import { useState } from "react";
import { toast } from "sonner";
import { X, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { CATEGORIES, CATEGORY_SUGGESTIONS, SWATCHES, catColor, catLabel, customCategories, sortCategories } from "@/lib/lore";
import { useInvalidate } from "@/lib/queries";

export const CategoryManager = ({ open, onOpenChange, uid }) => {
  const invalidate = useInvalidate();
  const [name, setName] = useState("");
  const [color, setColor] = useState(SWATCHES[0]);
  const custom = customCategories();
  const existing = [...CATEGORIES, ...custom.map((c) => c.name)].map((c) => c.toLowerCase());

  const add = async (n, c) => {
    try {
      await api.post(`/universes/${uid}/categories`, { name: n, color: c });
      invalidate(["universe", uid]);
      setName("");
      toast.success(`Category "${n}" added`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async (n) => {
    const { data } = await api.delete(`/universes/${uid}/categories/${encodeURIComponent(n)}`);
    invalidate(["universe", uid], ["entities", uid]);
    toast.success(`Removed "${n}"${data.reassigned ? ` · ${data.reassigned} entries moved to Lore` : ""}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="category-manager">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">Categories</DialogTitle>
          <DialogDescription>Add your own kinds of entries. The AI scribe will use them when sorting notes.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div>
            <div className="lf-label mb-2">Built-in</div>
            <div className="flex flex-wrap gap-1.5">{sortCategories(CATEGORIES).map((c) => <span key={c} className="text-xs rounded-sm px-2 py-1 border border-[#2D3442]" style={{ color: catColor(c) }}>{catLabel(c)}</span>)}</div>
          </div>
          <div>
            <div className="lf-label mb-2">Custom</div>
            {custom.length === 0 && <p className="text-sm text-[#A8A090]">None yet.</p>}
            <div className="flex flex-wrap gap-1.5" data-testid="custom-category-list">
              {custom.map((c) => (
                <span key={c.name} className="inline-flex items-center gap-1.5 text-xs rounded-sm px-2 py-1 border" style={{ color: c.color, borderColor: `${c.color}60` }}>
                  {c.name}<button onClick={() => remove(c.name)} data-testid={`remove-category-${c.name}`}><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); name.trim() && add(name.trim(), color); }} className="space-y-3">
            <div className="lf-label">New category</div>
            <div className="flex gap-2">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Weapon, Vehicle, Monster…" data-testid="category-name-input" />
              <Button type="submit" disabled={!name.trim()} data-testid="category-add-button"><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="flex gap-1.5">
              {SWATCHES.map((s) => (
                <button type="button" key={s} onClick={() => setColor(s)} data-testid={`category-color-${s}`}
                  className={`h-6 w-6 rounded-full transition-transform ${color === s ? "ring-2 ring-offset-2 ring-offset-[#13171F] ring-[#F3EBDD] scale-110" : ""}`} style={{ background: s }} />
              ))}
            </div>
          </form>
          <div>
            <div className="lf-label mb-2">Suggestions</div>
            <div className="flex flex-wrap gap-1.5">
              {CATEGORY_SUGGESTIONS.filter((s) => !existing.includes(s.name.toLowerCase())).map((s) => (
                <button key={s.name} onClick={() => add(s.name, s.color)} data-testid={`suggest-category-${s.name}`}
                  className="text-xs rounded-sm px-2.5 py-1 border border-dashed border-[#2D3442] text-[#A8A090] hover:text-[#F3EBDD] hover:border-[#D97706]/60 transition-colors">
                  + {s.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
